// Web Push enrolment for Tappin Online.
//
// This is the piece that makes notifications arrive when the tab is CLOSED or
// backgrounded (OS notification centre on desktop/mobile, PWA app-icon badge).
// In-page toasts, the bell, and the chime are handled by the existing
// `store/modules/notifications.js` over the websocket — this only adds the
// background/OS layer via a dedicated push-only service worker (public/push-sw.js).
//
// iOS note: web push on iPhone/iPad only works once the site is added to the
// Home Screen (installed PWA). Regular Safari tabs get the in-page experience
// only — an Apple platform limitation, not something we can work around.

import ApiClient from '@/client';

const PUSH_SW_URL = '/push-sw.js';
// Build-time fallback only. The authoritative key is fetched from the backend
// at runtime (see resolveVapidPublicKey) so the subscription always matches
// the key the SAME backend signs pushes with — this is what makes it work
// consistently across environments (local, dev, staging, prod) without having
// to keep each frontend build's env in sync with each backend's keypair.
const VAPID_PUBLIC_KEY_FALLBACK = process.env.VUE_APP_VAPID_PUBLIC_KEY || '';

let resolvedVapidKey = null;

// Fetch this backend's VAPID public key (cached for the session). Falls back
// to the build-time env var if the endpoint is unreachable.
async function resolveVapidPublicKey() {
  if (resolvedVapidKey) return resolvedVapidKey;
  try {
    const res = await ApiClient.getVapidPublicKey();
    const key = res && res.data && res.data.publicKey;
    if (key) {
      resolvedVapidKey = key;
      return resolvedVapidKey;
    }
  } catch (e) {
    /* fall back to the build-time key below */
  }
  resolvedVapidKey = VAPID_PUBLIC_KEY_FALLBACK;
  return resolvedVapidKey;
}

// The subscribe endpoint is authenticated (Platform policy). Only call it once
// the participant is logged in — i.e. a token exists for this event. Mirrors
// the token lookup in client.js's request interceptor. Calling it earlier 401s
// and (previously) tripped the global logout.
function hasEventAuthToken(eventId) {
  try {
    const user = JSON.parse(localStorage.getItem(`tokens_${eventId}`));
    return !!(user && user.access_token);
  } catch (e) {
    return false;
  }
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// VAPID keys travel as URL-safe base64; PushManager wants a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

let swRegistration = null;

// Register the push-only worker. Safe to call repeatedly.
export async function registerPushServiceWorker() {
  if (!isPushSupported()) return null;
  if (swRegistration) return swRegistration;
  try {
    swRegistration = await navigator.serviceWorker.register(PUSH_SW_URL);
    // Force an update check so a changed push-sw.js is adopted on the next
    // load instead of the browser keeping a stale worker (whose old click
    // handler is the usual reason "clicking the notification does nothing").
    try {
      await swRegistration.update();
    } catch (e) {
      /* ignore */
    }
    return swRegistration;
  } catch (e) {
    return null;
  }
}

function bytesEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// Return a subscription whose applicationServerKey matches the CURRENT VAPID
// key. If an existing subscription was created with a different (old) key —
// e.g. the backend keypair was rotated — it can never be delivered to, so we
// drop it here and let the caller create a fresh one. This is the usual cause
// of "notifications silently stopped working".
async function getValidSubscription(registration, vapidPublicKey) {
  const existing = await registration.pushManager.getSubscription();
  if (!existing) return null;

  const currentKey = urlBase64ToUint8Array(vapidPublicKey);
  const existingKey = existing.options && existing.options.applicationServerKey;
  const existingBytes = existingKey ? new Uint8Array(existingKey) : null;

  if (!bytesEqual(existingBytes, currentKey)) {
    try {
      await existing.unsubscribe();
    } catch (e) {
      /* ignore */
    }
    return null;
  }
  return existing;
}

function toServerPayload(subscription, eventId) {
  const json = subscription.toJSON();
  return {
    eventId,
    endpoint: subscription.endpoint,
    p256dh: json.keys && json.keys.p256dh,
    auth: json.keys && json.keys.auth,
    userAgent: navigator.userAgent
  };
}

// Ask permission (must be triggered by a user gesture), subscribe, and persist
// the subscription to the backend. Returns true when push is active.
export async function enablePush(eventId) {
  if (!isPushSupported() || !eventId) return false;
  if (!hasEventAuthToken(eventId)) return false;

  const vapidPublicKey = await resolveVapidPublicKey();
  if (!vapidPublicKey) return false;

  const registration = await registerPushServiceWorker();
  if (!registration) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  let subscription = await getValidSubscription(registration, vapidPublicKey);
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
  }

  try {
    await ApiClient.subscribeWebPush(toServerPayload(subscription, eventId));
    return true;
  } catch (e) {
    return false;
  }
}

// Opt out: unsubscribe in the browser and remove it server-side.
export async function disablePush() {
  if (!isPushSupported()) return;
  const registration = await registerPushServiceWorker();
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  const { endpoint } = subscription;
  try {
    await subscription.unsubscribe();
  } catch (e) {
    /* ignore */
  }
  try {
    await ApiClient.unsubscribeWebPush(endpoint);
  } catch (e) {
    /* ignore */
  }
}

// If notification permission is granted, make sure the backend has a current
// push subscription (creating one if needed, or refreshing after a browser
// key/endpoint rotation). Never prompts. Returns true only when a subscription
// was successfully saved server-side — the caller uses that to decide whether
// to retry later. Safe to call repeatedly (subscribe is idempotent; the POST
// upserts by endpoint).
export async function syncPushIfEnabled(eventId) {
  if (!isPushSupported() || !eventId) return false;
  if (!hasEventAuthToken(eventId)) return false;
  if (Notification.permission !== 'granted') return false;
  const vapidPublicKey = await resolveVapidPublicKey();
  if (!vapidPublicKey) return false;
  const registration = await registerPushServiceWorker();
  if (!registration) return false;
  let subscription = await getValidSubscription(registration, vapidPublicKey);
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
    } catch (e) {
      return false;
    }
  }
  try {
    await ApiClient.subscribeWebPush(toServerPayload(subscription, eventId));
    return true;
  } catch (e) {
    return false;
  }
}

// PWA app-icon unread badge (mirrors the tab/bell unread count). No-op where
// the Badging API is unavailable.
export function setAppBadge(count) {
  try {
    if (!('setAppBadge' in navigator)) return;
    if (count > 0) navigator.setAppBadge(count);
    else navigator.clearAppBadge();
  } catch (e) {
    /* ignore */
  }
}
